import { Collapse, styled, useTheme } from '@chronosphereio/chrono-ui';
import { hexToRgba } from '@chronosphereio/core';
import { useThrottle } from '@chronosphereio/shared';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ItemCallback, Layout, Layouts, Responsive, WidthProvider } from 'react-grid-layout';
import { VariableValue } from '@/core';
import { useReplaceVariablesInString } from '@/plugin-system';
import { GRID_LAYOUT_COLS, GRID_LAYOUT_SMALL_BREAKPOINT } from '../../constants';
import {
  PanelGroupDefinition,
  PanelGroupId,
  PanelGroupItemLayout,
  SplitModeOptions,
  useEditMode,
  useIsSplitModeEnabled,
  usePanelGroup,
  usePanelGroupActions,
} from '../../context';
import { PanelOptions } from '../Panel';
import { GridActions } from './GridActions';
import { GridContainer } from './GridContainer';
import { GridItem } from './GridItem';
import { GridTitle } from './GridTitle';
import { MoveDirection, MovePanelModal } from './MovePanelModal';
import { SplitPanelContent } from './SplitPanelContent';

const DEFAULT_MARGIN = 10;

const ResponsiveGridLayout = WidthProvider(Responsive);

const DRAG_OVERLAY_CLASS = 'drag-overlay';
const DRAGGED_OUT_CLASS = 'dragged-out';

const StyledResponsiveGridLayout = styled(ResponsiveGridLayout)(({ theme }) => {
  return {
    [`.${DRAG_OVERLAY_CLASS}`]: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      backgroundColor: hexToRgba(theme.palette.background.container, 0.6),
      zIndex: 5,
      display: 'none',
    },
    [`.react-draggable-dragging.${DRAGGED_OUT_CLASS} .${DRAG_OVERLAY_CLASS}`]: {
      display: 'block',
    },
  };
});

export interface GridLayoutProps {
  panelGroupId: PanelGroupId;
  panelOptions?: PanelOptions;
  panelGroupIndex: number;
  omitTitle?: boolean;
}

interface GridLayoutSplitModeWrapperProps {
  children: (args: GridLayoutSplitModeArgs) => React.ReactElement;
}

interface GridLayoutSplitModeArgs {
  isActionDisabled?: boolean;
  showMoveControls?: boolean;
  isMainPanelGroup?: boolean;
  splitVariableName?: string;
  splitVariableValue?: VariableValue;
}

const EMPTY_SPLIT_MODE_ARGS = {};

/**
 * Wrapper around the GridLayout content that provides arguments necessary for
 * enabling split mode.
 */
export function GridLayoutSplitModeWrapper({ children }: GridLayoutSplitModeWrapperProps) {
  const isSplitModeEnabled = useIsSplitModeEnabled();

  if (isSplitModeEnabled) {
    return (
      <SplitModeOptions>
        {({ isMainPanelGroup, splitVariableName, splitVariableValue }) => {
          return children({
            isActionDisabled: splitVariableName !== undefined && !isMainPanelGroup,
            showMoveControls: isMainPanelGroup || !splitVariableName,
            isMainPanelGroup,
            splitVariableName,
            splitVariableValue,
          });
        }}
      </SplitModeOptions>
    );
  }
  return children(EMPTY_SPLIT_MODE_ARGS);
}

function getCollapsedText(groupDefinition: PanelGroupDefinition): string {
  if (groupDefinition.kind === 'Grid') {
    const numberOfPanels = groupDefinition.itemLayouts.length;
    return `${numberOfPanels} panel${numberOfPanels !== 1 ? 's' : ''}`;
  }
  return '';
}

function getMutableLayout(layout: PanelGroupItemLayout[]): PanelGroupItemLayout[] {
  return layout.map((item) => ({ ...item }));
}

/**
 * Layout component that arranges children in a Grid based on the definition.
 */
export function GridLayout(props: GridLayoutProps) {
  const { panelGroupId, panelGroupIndex /*...others */ } = props;
  const { theme } = useTheme();
  const groupDefinition = usePanelGroup(panelGroupId);

  // Item layouts are a frozen object, and react-grid-layout can possible try to mutate directly,
  // so we will make a copy that is mutable
  const itemLayouts = useMemo(() => {
    if (groupDefinition.kind === 'Grid') {
      return getMutableLayout(groupDefinition.itemLayouts);
    }

    return [] as PanelGroupItemLayout[];
  }, [groupDefinition]);

  const { title: rawTitle } = groupDefinition;
  const { updatePanelGroupLayouts } = usePanelGroupActions(panelGroupId);

  // Note: when omitTitle is enabled, we ignore collapsed state because the toggle will no
  // longer be accessible. There's an edge case where a user changes the layout state
  // and collapses a panel group, then changes the layout again, causing state issues. For
  // code clarity, we are ignoring that issue here, can revisit if it becomes an issue.
  const [isOpen, setIsOpen] = useState(props.omitTitle || !groupDefinition.isCollapsed);
  const { isEditMode } = useEditMode();
  const omitTitle = props.omitTitle ?? false;

  const handleLayoutChange = useCallback(
    (currentLayout: Layout[], allLayouts: Layouts) => {
      // Using the value from `allLayouts` instead of `currentLayout` because of
      // a bug in react-layout-grid where `currentLayout` does not adjust properly
      // when going to a smaller breakpoint and then back to a larger breakpoint.
      // https://github.com/react-grid-layout/react-grid-layout/issues/1663
      const smallLayout = allLayouts[GRID_LAYOUT_SMALL_BREAKPOINT];
      if (smallLayout) {
        updatePanelGroupLayouts(getMutableLayout(smallLayout));
      }
    },
    [updatePanelGroupLayouts]
  );

  // TODO: this bit of code could use memoizing for perf, but it's tricky because
  // it is a hook and has a bunch of nested hooks inside it.
  const title = useReplaceVariablesInString(rawTitle) as string;

  const gridRef = useRef<HTMLDivElement>(null);

  const handleDrag = useCallback<ItemCallback>((layout, oldItem, newItem, placeholder, event, element) => {
    const gridElement = gridRef.current;

    if (gridElement && isElementOutOfContainer(element, gridElement)) {
      element.classList.add(DRAGGED_OUT_CLASS);
    } else {
      if (element.classList.contains(DRAGGED_OUT_CLASS)) {
        element.classList.remove(DRAGGED_OUT_CLASS);
      }
    }
  }, []);

  // Throttle the handleDrag function because it is called every move and
  // and setting the classes is really only a visual cue for the user so it's fine if it's not exact
  const handleDragThrottled = useThrottle(handleDrag, 100);

  const [movePanelItem, setMovePanelItem] = useState<{ panelId: string; moveDirection: MoveDirection }>();

  /*
    This key is used to force a re-render of the grid layout when the move panel to a new group
    modal is triggered.  ReactGridLayout keeps the updated layout as internal state and when this move
    flow happens, the layout will probably have changed with a panel being dragged somewhere new in the group
    to get it out of the bottom.  This key change will force it to rerender with the original layout that
    will then get updated once the panel is moved.
  */
  const [gridLayoutKey, setGridLayoutKey] = useState(0);

  const handleDragEnd = useCallback<ItemCallback>(
    (layout, oldItem, newItem, placeholder, event, element) => {
      const gridElement = gridRef.current;

      if (gridElement && isElementOutOfContainer(element, gridElement)) {
        const moveDirection = getMoveDirection(element, gridElement);

        // Trigger the modal
        setMovePanelItem({ panelId: newItem.i, moveDirection });
        setGridLayoutKey((prev) => prev + 1);
      } else {
        // Just update the layout
        handleLayoutChange(layout, { [GRID_LAYOUT_SMALL_BREAKPOINT]: layout });
      }
    },
    [handleLayoutChange, setGridLayoutKey]
  );

  return (
    <GridLayoutSplitModeWrapper>
      {({ isActionDisabled, showMoveControls, isMainPanelGroup, splitVariableName, splitVariableValue }) => {
        return (
          <GridContainer
            isOpen={isOpen}
            onCollapseButtonClick={() => setIsOpen((current) => !current)}
            actions={
              <GridActions
                groupId={panelGroupId}
                title={title}
                isActionDisabled={isActionDisabled}
                showMoveControls={showMoveControls}
                isMainPanelGroup={isMainPanelGroup}
              />
            }
            panelGroupIndex={panelGroupIndex}
          >
            {movePanelItem !== undefined && (
              <MovePanelModal
                itemKey={movePanelItem.panelId}
                moveDirection={movePanelItem.moveDirection}
                currentGroupId={panelGroupId}
                onClose={() => setMovePanelItem(undefined)}
              />
            )}

            {(!omitTitle || groupDefinition.kind === 'SplitPanel' || splitVariableName !== undefined) && (
              <GridTitle
                title={title}
                isOpen={isOpen}
                collapsedText={getCollapsedText(groupDefinition)}
                isSplitPanels={groupDefinition.kind === 'SplitPanel'}
                isMainPanelGroup={isMainPanelGroup}
                splitVariableName={
                  groupDefinition.kind === 'SplitPanel' ? groupDefinition.splitVariableName : splitVariableName
                }
                splitVariableValue={splitVariableValue}
              />
            )}
            <Collapse in={isOpen} unmountOnExit appear={false} data-testid="panel-group-content" sx={{ paddingY: 1 }}>
              {groupDefinition.kind === 'SplitPanel' && (
                <SplitPanelContent
                  panelOptions={props.panelOptions}
                  definition={groupDefinition}
                  panelGroupItemId={{ panelGroupId }}
                />
              )}
              {groupDefinition.kind === 'Grid' && (
                <div ref={gridRef}>
                  <StyledResponsiveGridLayout
                    key={gridLayoutKey}
                    /*
                    innerRef={
                      Setting this inner ref to anything (including just undefined) appears to cause
                      issues with the layout when used in conjuction with the width provider.
                    }
                    */
                    className={`layout`}
                    breakpoints={{ sm: theme.breakpoints.values.sm, xxs: 0 }}
                    cols={GRID_LAYOUT_COLS}
                    rowHeight={30}
                    draggableHandle={'.drag-handle'}
                    resizeHandles={['se']}
                    isDraggable={isEditMode}
                    isResizable={isEditMode}
                    onDrag={handleDragThrottled}
                    onDragStop={handleDragEnd}
                    margin={[DEFAULT_MARGIN, DEFAULT_MARGIN]}
                    containerPadding={[0, 0]}
                    layouts={{ [GRID_LAYOUT_SMALL_BREAKPOINT]: itemLayouts }}
                    onLayoutChange={handleLayoutChange}
                  >
                    {groupDefinition.itemLayouts.map(({ i, w, h }) => (
                      <GridItem
                        key={i}
                        panelOptions={props.panelOptions}
                        panelGroupItemId={{ panelGroupId, panelGroupItemLayoutId: i }}
                        gridLayout={{ w, h }}
                        splitVariableValue={splitVariableValue}
                      />
                    ))}
                  </StyledResponsiveGridLayout>
                </div>
              )}
            </Collapse>
          </GridContainer>
        );
      }}
    </GridLayoutSplitModeWrapper>
  );
}

/*
 * Check if the element is out of the container
 * @param dragElement the element being dragged
 * @param containerElement the container element of the item being dragged
 */
const isElementOutOfContainer = (dragElement: HTMLElement, containerElement: HTMLElement) => {
  const elementRect = dragElement.getBoundingClientRect();
  const containerRect = containerElement.getBoundingClientRect();
  const halfElementHeight = elementRect.height / 2;

  return (
    elementRect.top < containerRect.top - halfElementHeight ||
    elementRect.bottom > containerRect.bottom + halfElementHeight
  );
};

/*
 * Get the direction of the move
 * @param dragElement the element being dragged
 * @param containerElement the container element of the item being dragged
 */

const getMoveDirection = (dragElement: HTMLElement, containerElement: HTMLElement): MoveDirection => {
  const elementRect = dragElement.getBoundingClientRect();
  const containerRect = containerElement.getBoundingClientRect();

  if (elementRect.top < containerRect.top) {
    return 'up';
  } else {
    return 'down';
  }
};
